import {
  App,
  BasisAdresse,
  BasisRisikoort,
  Vorvertrag,
  VorvertragAdresse,
} from '@ps20-frontend/frontend-models';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { AngebotsId } from '@ps20-frontend/shop-integration-redux';
import { Umstellungsstatus } from '@ps20/bff-models';
import { equalAdressen } from '@ps20-frontend/vorschlagsdaten-lib';
import {
  selectFremdStandardProdukte,
  selectUmstellungsstatusForVorvertrag,
  selectVorvertraege,
} from '../../selectors/vorvertragsUebersicht.selector';

export function selectStandardVorvertragsAdressen(appId: App, appState: IAppState): BasisAdresse[] {
  const umstellbareStandardVorvertraege = selectStandardVorvertraege(appState)
    .filter(vorvertrag => isVorvertragForApp(vorvertrag, appId))
    .filter(vorvertrag => {
      const umstellungsStatus = selectUmstellungsstatusForVorvertrag(vorvertrag, appState);
      return [
        Umstellungsstatus.UMSTELLBAR,
        Umstellungsstatus.OPTIONAL_UMSTELLBAR,
        Umstellungsstatus.UMGESTELLT,
        Umstellungsstatus.UMSTELLBAR_ABER_PRODUKT_BEREITS_SELECTED,
        Umstellungsstatus.AKTUELL,
      ].includes(umstellungsStatus);
    });
  const risikoOrte: VorvertragAdresse[] = umstellbareStandardVorvertraege.reduce(
    (result, vorvertrag) => {
      const vorbelegungdaten = vorvertrag.vorbelegungdaten
        ? (vorvertrag.vorbelegungdaten.filter(
            v => v.forApp === appId,
          ) as unknown as BasisRisikoort[])
        : [];
      return result.concat(
        vorbelegungdaten.filter(({ risikoort }) => risikoort).map(vdaten => vdaten.risikoort),
      );
    },
    [],
  );
  const vorvertragAdressen: BasisAdresse[] = risikoOrte.map(risikoort => ({
    plz: risikoort.plz,
    ort: risikoort.ort,
    strasse: risikoort.strasse,
    hausnummer: risikoort.hausnummer,
  }));

  return vorvertragAdressen.reduce((adressen, adresse) => {
    if (!includesAdresse(adressen, adresse)) {
      return [...adressen, adresse];
    }
    return adressen;
  }, []);
}

function selectStandardVorvertraege(appState: IAppState): Vorvertrag[] {
  return [...selectVorvertraege(appState), ...selectFremdStandardProdukte(appState)];
}

function isVorvertragForApp(vorvertrag: Vorvertrag, app: App): boolean {
  return vorvertrag.produktIds.includes(app);
}

function includesAdresse(adressen: BasisAdresse[], adresse: BasisAdresse): boolean {
  return adressen.some(a => equalAdressen(a, adresse));
}

export const standardVorvertragWithEqualAdressExists = (
  appId: App,
  adresse: BasisAdresse,
  appState: IAppState,
): boolean => {
  const vorvertragsAdressen = selectStandardVorvertragsAdressen(appId, appState);
  return includesAdresse(vorvertragsAdressen, adresse);
};

export const standardVorvertraegeForAngebot = (
  { appId, angebotsId }: AngebotsId,
  appState: IAppState,
): Vorvertrag[] => {
  const angebotInWarenkorb = appState.warenkorb.find(
    wp => wp.appId === appId && wp.angebotsId === angebotsId,
  );
  const angebotsVorvertragsnummern = angebotInWarenkorb?.vorvertragsNummern || [];
  return selectStandardVorvertraege(appState).filter(vorvertrag =>
    angebotsVorvertragsnummern.some(
      ({ vertragsnummer, spLaufnummer }) =>
        vertragsnummer === vorvertrag.vertragsnummer && spLaufnummer === vorvertrag.spLaufnummer,
    ),
  );
};

export const adresseEqualsVorvertragForAngebotAdresse = ({
  appId,
  appState,
  angebotsId,
  adresse,
}: {
  appId: App;
  appState: IAppState;
  angebotsId: string;
  adresse: BasisAdresse;
}): boolean => {
  const vorvertraege = standardVorvertraegeForAngebot({ appId: appId, angebotsId }, appState);
  return vorvertraege.some(vorvertrag => {
    const vorbelegungdaten = vorvertrag?.vorbelegungdaten?.find(
      v => v.forApp === appId,
    ) as unknown as BasisRisikoort;
    return equalAdressen(vorbelegungdaten?.risikoort, adresse);
  });
};

export const isStandardVorvertragWithSameAdresseExistant = (
  appId: App,
  angebotsId: string,
  adresse: BasisAdresse,
  appState: IAppState,
): boolean => {
  if (![App.VHV, App.VHV_V2, App.VWG, App.VWG_V2].includes(appId) || adresse == null) {
    return false;
  }

  if (
    adresseEqualsVorvertragForAngebotAdresse({
      appId,
      angebotsId,
      adresse,
      appState,
    })
  ) {
    return false;
  }

  return standardVorvertragWithEqualAdressExists(appId, adresse, appState);
};
